import {
  GapFillingOptions,
  PointSizeOptions,
  useRenderingSettings,
} from "@/components/common/rendering-settings-context";
import { PointCloudObject } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import { selectModeName } from "@/store/mode-selectors";
import { selectPointCloudNormalDirection } from "@/store/view-options/view-options-selectors";
import {
  attachPass,
  EyeDomePass,
  GapFillPass,
  LodPointCloudRendererBase,
  selectIElementWorldTransform,
  useAssignClippingPlanes,
} from "@faro-lotv/app-component-toolbox";
import { GUID } from "@faro-lotv/foundation";
import {
  AdaptivePointsMaterial,
  GapFillOptimizedFor,
  LodPointCloud,
  NormalDirectionPass,
} from "@faro-lotv/lotv";
import { useFrame, useThree } from "@react-three/fiber";
import { EventHandlers } from "@react-three/fiber/dist/declarations/src/core/events";
import { useEffect, useState, type JSX } from "react";
import { Plane } from "three";
import { SurfaceWandEffects } from "@/tools/surface-wand/surface-wand-effects";
import { CadClashCoverageEffect } from "@/tools/cad-analysis/cad-clash-coverage-effect";
import { CadUnmatchedOpacityEffect } from "@/tools/cad-analysis/cad-unmatched-opacity-effect";

export type PointCloudProps = EventHandlers & {
  /** Object to render */
  pointCloud: PointCloudObject;

  /** Point cloud opacity */
  opacity?: number;

  /**
   * Whether the point cloud should answser to pointer events via raycasting
   *
   * @default true By default the point cloud handles pointer events via efficient raycasting on the loaded LOD nodes
   */
  raycastEnabled?: boolean;

  /** Optional clipping planes */
  clippingPlanes?: Plane[];

  /** Optional list of scans to hide when rendering this LOD point cloud */
  hiddenScans?: GUID[];
};

/**
 * @returns the PointCloud in the correct world position
 */
export function PointCloudRenderer({
  pointCloud,
  opacity = 1,
  raycastEnabled = true,
  clippingPlanes,
  hiddenScans,
  ...rest
}: PointCloudProps): JSX.Element {
  const transform = useAppSelector(
    selectIElementWorldTransform(pointCloud.iElement.id),
  );

  useAssignClippingPlanes(pointCloud, clippingPlanes);

  const { pointSize } = useRenderingSettings();

  // Reacting to changes in pointSize
  useEffect(() => {
    const mat = pointCloud.material;
    if (!(mat instanceof AdaptivePointsMaterial)) return;

    switch (pointSize) {
      case PointSizeOptions.Adaptive:
        mat.targetMinMaxSize = { minSize: 2, maxSize: 6 };
        break;
      case PointSizeOptions.AdaptiveSharper:
        mat.targetMinMaxSize = { minSize: 1, maxSize: 3 };
        break;
      case PointSizeOptions.FixedOne:
        mat.targetMinMaxSize = { minSize: 1, maxSize: 1 };
        break;
      case PointSizeOptions.FixedTwo:
        mat.targetMinMaxSize = { minSize: 2, maxSize: 2 };
        break;
    }
  }, [pointSize, pointCloud]);

  // Reacting to changes in the list of scans to hide
  useEffect(() => {
    const mat = pointCloud.material;
    if (!(mat instanceof AdaptivePointsMaterial) || !hiddenScans) return;
    const scans: number[] = [];
    for (const scanId of hiddenScans) {
      const id = pointCloud.scanIdsToIndex?.[scanId];
      if (id !== undefined) {
        scans.push(id);
      }
    }
    mat.hiddenScans = scans;
  }, [hiddenScans, pointCloud]);

  return (
    <group {...transform}>
      <LodPointCloudRendererBase
        pointCloud={pointCloud}
        opacity={opacity}
        raycastEnabled={raycastEnabled}
        {...rest}
      />
    </group>
  );
}

type PointCloudDefaultEffectsProps = {
  /** The PointCloud that will be rendered by these Effects */
  pointCloud: LodPointCloud | null;
};

/** @returns the set of default effects for PointClouds to use in an EffectPipeline */
export function PointCloudDefaultEffects({
  pointCloud,
}: PointCloudDefaultEffectsProps): JSX.Element {
  const { gapFilling, eyeDomeOn } = useRenderingSettings();

  const isMonochrome = pointCloud?.monochrome ?? false;

  return (
    <>
      <GapFillPass
        enabled={gapFilling !== GapFillingOptions.Disabled}
        optimizedFor={
          gapFilling === GapFillingOptions.Speed
            ? GapFillOptimizedFor.Speed
            : GapFillOptimizedFor.GapSize
        }
      />
      <PointCloudNormalDirectionPass pointCloud={pointCloud} />
      <EyeDomePass
        enabled={isMonochrome || eyeDomeOn}
        strength={isMonochrome ? 0.5 : 0.1}
        showBlackOutlines={eyeDomeOn}
      />
      <SurfaceWandEffects pointCloud={pointCloud} />
      <CadClashCoverageEffect pointCloud={pointCloud} />
      <CadUnmatchedOpacityEffect pointCloud={pointCloud} />
    </>
  );
}

/** @returns Depth-based coloring for the point-cloud subscene only. */
function PointCloudNormalDirectionPass({
  pointCloud,
}: PointCloudDefaultEffectsProps): JSX.Element {
  const camera = useThree((state) => state.camera);
  const mode = useAppSelector(selectModeName);
  const normalDirection = useAppSelector(selectPointCloudNormalDirection);
  const [pass] = useState(() => new NormalDirectionPass(camera));
  useEffect(() => () => pass.dispose(), [pass]);
  useFrame(() => {
    pass.camera = camera;
    const material = pointCloud?.material;
    const hasAnalysis =
      material instanceof AdaptivePointsMaterial &&
      material.defines.USE_COLORMAP === 1;
    pass.enabled =
      !!pointCloud &&
      normalDirection &&
      !hasAnalysis &&
      (mode === "overview" || mode === "walk");
  });
  return <primitive object={pass} attach={attachPass} />;
}
